import { type ReactNode } from 'react'

export type IconKey =
  | 'dashboard' | 'integrations' | 'modules' | 'state' | 'providers'
  | 'plans' | 'policies' | 'graph' | 'tokens' | 'feedback' | 'chevron' | 'coder' | 'mcp' | 'driftdetection' | 'audit'

export interface NavItem {
  to: string
  label: string
  icon: IconKey
  end?: boolean
  tooltip?: string
  matchPrefix?: string
}

// Inline SVG icons - 16x16, stroke=currentColor so they inherit nav item
// colour and the active/hover transitions. No icon library (matches the app's
// Unicode-glyph approach elsewhere).
export const svg = (children: ReactNode) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    {children}
  </svg>
)

/**
 * One glyph per concept, kept consistent with the dependency graph's node shapes:
 * cylinder = state, cube = module, chip = provider, shield-with-check = policy.
 *
 * Providers used to wear the shield-with-check and Policies a plain shield, which left two
 * shields in one sidebar and said "trusted" where it meant "compiled binary plugin". The check
 * belongs with policies, where a pass IS a check.
 */
export const NAV_ICONS: Record<IconKey, ReactNode> = {
  dashboard: svg(<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>),
  integrations: svg(<><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>),
  modules: svg(<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7l8.7 5 8.7-5" /><path d="M12 22V12" /></>),
  state: svg(<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" /></>),
  providers: svg(<><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" /></>),
  plans: svg(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>),
  policies: svg(<><path d="M12 2 3 7v5c0 5 3.8 8.4 9 10 5.2-1.6 9-5 9-10V7l-9-5z" /><path d="m9 12 2 2 4-4" /></>),
  graph: svg(<><circle cx="5" cy="6" r="2.5" /><circle cx="19" cy="6" r="2.5" /><circle cx="12" cy="18" r="2.5" /><path d="M6.8 7.7 10.5 16m7-8.3L13.5 16" /></>),
  tokens: svg(<><circle cx="8" cy="15" r="4" /><path d="M10.85 12.15 19 4l2 2-2 2 2 2-2 2-2-2-1.5 1.5" /></>),
  feedback: svg(<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>),
  chevron: svg(<path d="m9 18 6-6-6-6" />),
  coder: svg(<><rect x="2.5" y="4" width="19" height="16" rx="2" /><path d="m7 10 2.5 2L7 14" /><path d="M12.5 15h4" /></>),
  mcp: svg(<><path d="M9 2v5" /><path d="M15 2v5" /><path d="M6 7h12v4a6 6 0 0 1-12 0V7z" /><path d="M12 17v5" /></>),
  driftdetection: svg(<><path d="M3 12h4l2-7 4 14 2-7h6" /><path d="M17 5l3 3-3 3" /></>),
  audit: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13l2 2 4-4" /></>),
}

